import { homeV2Eyebrow, homeV2Glass, homeV2Text, homeV2Type } from '@/lib/home/homeV2Theme'
import { cn } from '@/lib/utils'
import type { CSSProperties } from 'react'
import {
  surfaceForegroundStyle,
  v2ModalShellDefaultTextStyle as sharedShellDefault,
} from '@/lib/design/surfaceForeground'

/** Typography + surface tokens for modal interiors — matches Home / Play V2. */
export const v2ModalStyles = {
  eyebrow: { ...homeV2Type(homeV2Text.section), ...homeV2Eyebrow() },
  headline: homeV2Type(homeV2Text.headline),
  body: homeV2Type(homeV2Text.body),
  primary: homeV2Type(homeV2Text.primary),
  metadata: homeV2Type(homeV2Text.metadata),
  muted: homeV2Type(homeV2Text.muted),
  warm: homeV2Type(homeV2Text.warm),
  link: homeV2Type(homeV2Text.link),
  nestedCard: {
    borderColor: 'rgba(255, 236, 210, 0.22)',
    background:
      'linear-gradient(165deg, rgba(32, 28, 30, 0.36) 0%, rgba(44, 38, 40, 0.20) 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255, 248, 238, 0.22)',
    // Nested dark glass must re-assert readable FG (beats body --wag-dark).
    ...surfaceForegroundStyle.darkGlass,
  },
  /** Glass field fill — inline on inputs/selects for reliable cross-browser rendering. */
  field: {
    background:
      'linear-gradient(165deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.05) 100%)',
    borderColor: 'rgba(255, 236, 210, 0.26)',
    boxShadow: 'inset 0 1px 0 rgba(255, 248, 238, 0.14)',
    color: homeV2Text.primary,
  },
  divider: { borderColor: 'rgba(255, 236, 210, 0.16)' },
  callout: {
    borderColor: 'rgba(255, 236, 210, 0.24)',
    background: 'rgba(251, 191, 36, 0.08)',
  },
} as const

/**
 * Default text color on dark champagne glass shells.
 * Beats `body { color: var(--wag-dark) }` inheritance for unstyled children.
 * Canonical source: lib/design/surfaceForeground.ts
 */
export const v2ModalShellDefaultTextStyle: CSSProperties = sharedShellDefault

/** Modal shell geometry — locked V2 standard (docs/V2_MODAL_DESIGN_STANDARD.md). */
export const v2ModalGeometry = {
  centerRadius: 'rounded-[28px]',
  sheetTopRadius: 'rounded-t-[32px]',
  sheetBottomFlush: 'rounded-b-none',
  fieldRadius: 'rounded-[16px]',
  nestedRadius: 'rounded-[20px]',
  centerRadiusPx: 28,
  sheetTopRadiusPx: 32,
} as const

/** Inline radius for the shell clip host — reliable on iOS / overflow scroll. */
export function v2ModalShellRadiusStyle(presentation: 'center' | 'sheet'): CSSProperties {
  if (presentation === 'sheet') {
    return {
      borderTopLeftRadius: v2ModalGeometry.sheetTopRadiusPx,
      borderTopRightRadius: v2ModalGeometry.sheetTopRadiusPx,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    }
  }
  return { borderRadius: v2ModalGeometry.centerRadiusPx }
}

/** Outer radius on ring host — 28px center modal, 32px sheet top (bottom flush). */
export function v2ModalShellRadiusClassName(presentation: 'center' | 'sheet'): string {
  return presentation === 'sheet'
    ? cn(v2ModalGeometry.sheetTopRadius, v2ModalGeometry.sheetBottomFlush)
    : v2ModalGeometry.centerRadius
}

/** Inner radius on clip host — 1px inset from ring (27px / 31px top). Never use square corners inside shell. */
export function v2ModalShellInnerRadiusClassName(presentation: 'center' | 'sheet'): string {
  return presentation === 'sheet' ? 'rounded-t-[31px] rounded-b-none' : 'rounded-[27px]'
}

export function v2ModalShellInnerRadiusStyle(presentation: 'center' | 'sheet'): CSSProperties {
  if (presentation === 'sheet') {
    const inner = v2ModalGeometry.sheetTopRadiusPx - 1
    return {
      borderTopLeftRadius: inner,
      borderTopRightRadius: inner,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    }
  }
  return { borderRadius: v2ModalGeometry.centerRadiusPx - 1 }
}

/**
 * Gradient stroke for ring host (p-px). Replaces CSS border on blur layer to avoid WebKit corner artifacts.
 * See docs/V2_MODAL_DESIGN_STANDARD.md — do not move this onto the clip host as a CSS border.
 */
export const v2ModalShellBorderGradient =
  'linear-gradient(165deg, rgba(255, 228, 185, 0.52) 0%, rgba(255, 200, 140, 0.16) 48%, rgba(255, 180, 110, 0.30) 100%)'

export const v2ModalFieldBaseClassName = cn(
  'w-full border px-3 py-2.5 text-sm backdrop-blur-[10px]',
  'text-white placeholder:text-white/50',
  'focus:outline-none focus:ring-2 focus:ring-amber-400/45 focus:border-amber-200/40',
  'disabled:cursor-not-allowed disabled:opacity-60',
  v2ModalGeometry.fieldRadius
)

export const v2ModalInputClassName = v2ModalFieldBaseClassName

export const v2ModalSelectClassName = cn(
  v2ModalFieldBaseClassName,
  'appearance-none bg-none pr-10',
  '[&>option]:bg-[#2a2426] [&>option]:text-white'
)

export const v2ModalTextareaClassName = cn(v2ModalFieldBaseClassName, 'min-h-[80px] resize-y')

export function v2ModalFieldStyle(): CSSProperties {
  return {
    background: v2ModalStyles.field.background,
    borderColor: v2ModalStyles.field.borderColor,
    boxShadow: v2ModalStyles.field.boxShadow,
    color: v2ModalStyles.field.color,
  }
}

export function v2ModalSelectStyle(): CSSProperties {
  return {
    ...v2ModalFieldStyle(),
    colorScheme: 'dark',
  }
}

/** Restyle legacy form markup inside V2 bottom sheets (Dog Profile). */
export const v2ModalFormShellClassName = cn(
  '[&_textarea]:rounded-[16px] [&_textarea]:border [&_textarea]:border-white/20 [&_textarea]:text-white',
  '[&_textarea]:placeholder:text-white/50 [&_textarea]:backdrop-blur-[10px]',
  '[&_input:not([type=checkbox]):not([type=radio])]:rounded-[16px]',
  '[&_input:not([type=checkbox]):not([type=radio])]:border [&_input:not([type=checkbox]):not([type=radio])]:border-white/20',
  '[&_input:not([type=checkbox]):not([type=radio])]:text-white',
  '[&_input:not([type=checkbox]):not([type=radio])]:placeholder:text-white/50',
  '[&_label]:text-[10px] [&_label]:font-semibold [&_label]:uppercase [&_label]:tracking-[0.12em]',
  '[&_label]:text-[rgba(255,236,210,0.72)]'
)

/** Selected chip label — beats body --wag-dark inheritance and nested inline typography. */
const v2ModalChipSelectedTextClasses =
  '!text-[#FFF9F3] [&_*]:!text-[#FFF9F3] [&_svg]:!text-[#FFF9F3] [&_span]:!text-[#FFF9F3]'

export function v2ModalChipClassName(selected: boolean): string {
  return cn(
    'rounded-full border px-3 py-1.5 text-xs font-medium transition-all',
    selected
      ? cn(
          v2ModalChipSelectedTextClasses,
          'border-amber-200/55 bg-amber-400/32 font-semibold shadow-[inset_0_1px_0_rgba(255,248,238,0.28)]'
        )
      : 'border-white/15 bg-white/[0.06] text-white/80 hover:border-amber-200/30 hover:bg-white/[0.10]'
  )
}

/** Inline selected color — reliable over inherited --wag-dark on native buttons. */
export function v2ModalChipStyle(selected: boolean): CSSProperties | undefined {
  if (!selected) return undefined
  return { color: homeV2Text.headline }
}

export function v2ModalSectionIconWellStyle(): CSSProperties {
  return {
    background: homeV2Glass.whisper.background,
    borderColor: homeV2Glass.whisper.border,
    boxShadow: homeV2Glass.whisper.shadow,
    color: homeV2Text.warm,
  }
}

export function v2ModalStatusChipClassName(
  color: 'amber' | 'green' | 'red' | 'gray'
): string {
  const tone = {
    amber: 'border-amber-200/40 bg-amber-400/18 text-amber-100',
    green: 'border-emerald-200/40 bg-emerald-400/18 text-emerald-100',
    red: 'border-red-200/40 bg-red-400/18 text-red-100',
    gray: 'border-white/20 bg-white/[0.08] text-white/75',
  }[color]
  return cn(
    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium',
    tone
  )
}

export function v2ModalDangerChipClassName(selected: boolean): string {
  return cn(
    'rounded-full border px-3 py-1.5 text-xs font-medium transition-all',
    selected
      ? cn(
          v2ModalChipSelectedTextClasses,
          'border-red-200/55 bg-red-600/48 font-semibold shadow-[inset_0_1px_0_rgba(255,248,238,0.28)]'
        )
      : 'border-white/15 bg-white/[0.06] text-white/80 hover:border-red-300/40 hover:bg-red-500/10'
  )
}

/** Inline selected color for hazard / safety reporting chips. */
export function v2ModalDangerChipStyle(selected: boolean): CSSProperties | undefined {
  if (!selected) return undefined
  return { color: homeV2Text.headline }
}

export const v2ModalCheckboxClassName =
  'h-4 w-4 shrink-0 rounded border-white/25 bg-white/[0.06] text-amber-400 focus:ring-amber-400/40 focus:ring-offset-0'

/** Semantic error surface for modal interiors — warm glass, not harsh orange text. */
export function v2ModalErrorBannerStyle(): CSSProperties {
  return {
    border: '1px solid rgba(248, 113, 113, 0.35)',
    background: 'rgba(248, 113, 113, 0.12)',
    color: '#FECACA',
  }
}

/** Semantic warning / empty-state callout inside V2 modals. */
export function v2ModalWarningCalloutStyle(): CSSProperties {
  return {
    border: '1px solid rgba(251, 191, 36, 0.35)',
    background: 'rgba(251, 191, 36, 0.12)',
    color: homeV2Text.body,
  }
}

export const v2ModalStatusBannerClassName = cn(
  'rounded-[16px] px-3 py-2.5 text-sm leading-relaxed text-center'
)

export const v2ModalDividerClassName = 'border-t pt-2'

export const v2ModalBodyTextClassName = 'text-sm leading-relaxed'

export const v2ModalMetadataTextClassName = 'text-xs leading-relaxed'
